import { Link } from "react-router"
import cx from "classnames"

import OrderedList from "../../components/OrderedList"
import EligibilityQuizBtnContainer from "../EligibilityQuiz/EligibilityQuizBtnContainer"
import EligibilityQuizHeader from "../EligibilityQuiz/EligibilityQuizHeader"
import { useAccountUrlPrefix } from "../../hooks"
import { EligibleProgramType } from "@/types/govtAppWizard"
import ELIGIBLE from "../../images/govt-eligibility-quiz-eligible.png"
import INELIGIBLE from "../../images/govt-eligibility-quiz-ineligible.png"

export interface GovtWizardQuizCompletedProps {
  eligibleProgramType: EligibleProgramType | undefined
  handleCompleted: (completed: boolean) => void
  handleQuizStep: (quizStep: number) => void
  handleNext: () => void
}

const GovtWizardQuizCompleted = ({
  eligibleProgramType,
  handleCompleted,
  handleQuizStep,
  handleNext,
}: GovtWizardQuizCompletedProps) => {
  const accountUrlPrefix = useAccountUrlPrefix()

  const isIneligible = eligibleProgramType === "ineligible"

  const handleStartOver = () => {
    handleQuizStep(0)
    handleCompleted(false)
  }

  return (
    <div className="container xl:max-w-[1440px] flex justify-between gap-8 pt-12 md:pt-[128px] pb-[160px]">
      <div className="hidden md:block w-auto max-w-[528px]">
        <img
          src={isIneligible ? INELIGIBLE : ELIGIBLE}
          alt="Government eligibility quiz"
          className="w-auto max-h-[502px] md:mb-10"
        />
      </div>

      <div className="md:w-[500px] max-w-full">
        <div className="mt-2">
          <EligibilityQuizHeader
            eyebrow="Eligibility check"
            text={
              eligibleProgramType === "eqip"
                ? "Congrats! You're Eligible for the NRCS EQIP program"
                : eligibleProgramType === "csp"
                  ? "Congrats! You're Eligible for the NRCS CSP program"
                  : "You're not eligible for NRCS programs, but you are eligible for others"
            }
            imageUrl={isIneligible ? INELIGIBLE : ELIGIBLE}
            quizType="Government eligibility quiz"
          />

          <div className="mb-12">
            {isIneligible ? (
              <>
                <p className="text-charcoal-500 leading-[130%] tracking-[0.32px] mt-8">
                  Based on your answers, your land is not eligible for this NRCS
                  EQIP program, but you do have eligible programs in this
                  category.
                </p>

                <p className="text-charcoal-500 leading-[130%] tracking-[0.32px] mt-3">
                  Browse eligible programs to find the one that’s just right for
                  you and your land.
                </p>
              </>
            ) : (
              <OrderedList
                list={[
                  "Review the stacks of practices NCX recommends for your land.",
                  "Choose how you want to work with NCX to accomplish your land program goals. You can manage the process on your own or get additional help from NCX’s team of forestry experts.",
                  "Follow through with NRCS at your local office with paperwork and start taking action on your new programs!",
                ]}
                className="mt-6"
                itemClass="font-body text-sm !leading-[140%] !tracking-[0.32px] last:mb-0"
                centered
              />
            )}
          </div>

          <EligibilityQuizBtnContainer>
            <button
              type="button"
              className="gov-app-wizard-start-over btn2 btn2-outline-primary font-bold tracking-[0.32px] md:self-start"
              onClick={handleStartOver}
            >
              Start Over
            </button>

            {isIneligible ? (
              <Link
                to={`${accountUrlPrefix}/${"programs/page/1"}`}
                className={cx(
                  "gov-app-wizard-quiz-view-eligible-programs",
                  "btn2 btn2-primary text-center font-semibold md:self-start"
                )}
                state={{}}
              >
                View Eligible Programs
              </Link>
            ) : (
              <button
                type="button"
                className="gov-app-wizard-see-stack-options btn2 btn2-primary text-center font-semibold md:self-start"
                onClick={handleNext}
              >
                See Stack Options
              </button>
            )}
          </EligibilityQuizBtnContainer>
        </div>
      </div>
    </div>
  )
}

export default GovtWizardQuizCompleted
